import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getApiClient } from '../client';
import type { PaginatedResponse } from '../types/common';
import type {
  Incident,
  IncidentListItem,
  IncidentCreatePayload,
  IncidentUpdatePayload,
  IncidentComment,
  IncidentCommentCreatePayload,
  IncidentHistory,
  IncidentImage,
  IncidentImageType,
} from '../types/incident';

/** Query keys */
export const incidentsKeys = {
  all: ['incidents'] as const,
  lists: () => [...incidentsKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...incidentsKeys.lists(), filters] as const,
  details: () => [...incidentsKeys.all, 'detail'] as const,
  detail: (id: string) => [...incidentsKeys.details(), id] as const,
  comments: (id: string) => [...incidentsKeys.detail(id), 'comments'] as const,
  history: (id: string) => [...incidentsKeys.detail(id), 'history'] as const,
  images: (id: string) => [...incidentsKeys.detail(id), 'images'] as const,
};

/** Fetch incidents with filters */
export function useIncidents(filters: Record<string, unknown> = {}) {
  return useQuery({
    queryKey: incidentsKeys.list(filters),
    queryFn: async () => {
      const client = getApiClient();
      const response = await client.get<PaginatedResponse<IncidentListItem>>('/api/v1/incidents/', {
        params: filters,
      });
      return response.data;
    },
  });
}

/** Fetch a single incident */
export function useIncident(id: string) {
  return useQuery({
    queryKey: incidentsKeys.detail(id),
    queryFn: async () => {
      const client = getApiClient();
      const response = await client.get<Incident>(`/api/v1/incidents/${id}/`);
      return response.data;
    },
    enabled: !!id,
  });
}

/** Create an incident */
export function useCreateIncident() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: IncidentCreatePayload) => {
      const client = getApiClient();
      const response = await client.post<Incident>('/api/v1/incidents/', payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: incidentsKeys.lists() });
    },
  });
}

/** Update an incident */
export function useUpdateIncident() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: IncidentUpdatePayload }) => {
      const client = getApiClient();
      const response = await client.patch<Incident>(`/api/v1/incidents/${id}/`, payload);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: incidentsKeys.lists() });
      queryClient.setQueryData(incidentsKeys.detail(data.id), data);
    },
  });
}

/** Assign incident to a user */
export function useAssignIncident() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, assigneeId }: { id: string; assigneeId: string | null }) => {
      const client = getApiClient();
      const response = await client.post<Incident>(`/api/v1/incidents/${id}/assign/`, {
        assigned_to_id: assigneeId,
      });
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: incidentsKeys.lists() });
      queryClient.setQueryData(incidentsKeys.detail(data.id), data);
    },
  });
}

/** Update incident status */
export function useUpdateIncidentStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, status, notes }: { id: string; status: string; notes?: string }) => {
      const client = getApiClient();
      const response = await client.post<Incident>(`/api/v1/incidents/${id}/status/`, {
        status,
        notes,
      });
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: incidentsKeys.lists() });
      queryClient.setQueryData(incidentsKeys.detail(data.id), data);
      queryClient.invalidateQueries({ queryKey: incidentsKeys.history(data.id) });
    },
  });
}

/** Fetch incident comments */
export function useIncidentComments(incidentId: string) {
  return useQuery({
    queryKey: incidentsKeys.comments(incidentId),
    queryFn: async () => {
      const client = getApiClient();
      const response = await client.get<IncidentComment[]>(
        `/api/v1/incidents/${incidentId}/comments/`
      );
      return response.data;
    },
    enabled: !!incidentId,
  });
}

/** Add comment to incident */
export function useAddIncidentComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ incidentId, payload }: { incidentId: string; payload: IncidentCommentCreatePayload }) => {
      const client = getApiClient();
      const response = await client.post<IncidentComment>(
        `/api/v1/incidents/${incidentId}/comments/`,
        payload
      );
      return response.data;
    },
    onSuccess: (_, { incidentId }) => {
      queryClient.invalidateQueries({ queryKey: incidentsKeys.comments(incidentId) });
    },
  });
}

/** Fetch incident status history */
export function useIncidentHistory(incidentId: string) {
  return useQuery({
    queryKey: incidentsKeys.history(incidentId),
    queryFn: async () => {
      const client = getApiClient();
      const response = await client.get<IncidentHistory[]>(
        `/api/v1/incidents/${incidentId}/history/`
      );
      return response.data;
    },
    enabled: !!incidentId,
  });
}

/** Fetch incident images */
export function useIncidentImages(incidentId: string) {
  return useQuery({
    queryKey: incidentsKeys.images(incidentId),
    queryFn: async () => {
      const client = getApiClient();
      const response = await client.get<IncidentImage[]>(
        `/api/v1/incidents/${incidentId}/images/`
      );
      return response.data;
    },
    enabled: !!incidentId,
  });
}

/** Upload image to incident */
export function useUploadIncidentImage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      incidentId,
      image,
      imageType = 'progress',
      caption = ''
    }: {
      incidentId: string;
      image: File;
      imageType?: IncidentImageType;
      caption?: string;
    }) => {
      const client = getApiClient();
      const formData = new FormData();
      formData.append('image', image);
      formData.append('image_type', imageType);
      if (caption) formData.append('caption', caption);

      const response = await client.post<IncidentImage>(
        `/api/v1/incidents/${incidentId}/images/`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    },
    onSuccess: (_, { incidentId }) => {
      queryClient.invalidateQueries({ queryKey: incidentsKeys.images(incidentId) });
    },
  });
}

/** Delete an incident image */
export function useDeleteIncidentImage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ incidentId, imageId }: { incidentId: string; imageId: string }) => {
      const client = getApiClient();
      await client.delete(`/api/v1/incidents/${incidentId}/images/${imageId}/`);
    },
    onSuccess: (_, { incidentId }) => {
      queryClient.invalidateQueries({ queryKey: incidentsKeys.images(incidentId) });
    },
  });
}

